import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuth, fetchAndDownload } from '../../utils/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function SalesView({ user, handleApiError, showNotification }) {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState(null);
    const [isBulkDelete, setIsBulkDelete] = useState(false);
    const [selectedSales, setSelectedSales] = useState([]);
    const [expandedSaleId, setExpandedSaleId] = useState(null);

    const fetchSales = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAuth(`${API_URL}/api/sales`);
            if (res.ok) setSales(await res.json());
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }, [handleApiError]);

    useEffect(() => {
        fetchSales();
    }, [fetchSales]);

    const triggerDeleteSale = (saleId) => {
        setIsBulkDelete(false);
        setSaleToDelete(saleId);
        setDeleteModalVisible(true);
    };

    const triggerBulkDelete = () => {
        setIsBulkDelete(true);
        setSaleToDelete(null);
        setDeleteModalVisible(true);
    };

    const confirmDeleteSale = async () => {
        try {
            if (isBulkDelete) {
                if (selectedSales.length === 0) return;
                const res = await fetchAuth(`${API_URL}/api/sales/bulk-delete`, {
                    method: 'POST',
                    body: JSON.stringify({ saleIds: selectedSales })
                });
                if (!res.ok) throw new Error((await res.json()).message);
                showNotification(`${selectedSales.length} sales deleted successfully.`, 'success');
                setSelectedSales([]);
            } else {
                if (!saleToDelete) return;
                const res = await fetchAuth(`${API_URL}/api/sales/${saleToDelete}`, { method: 'DELETE' });
                if (!res.ok) throw new Error((await res.json()).message);
                showNotification('Sale deleted successfully.', 'success');
            }
            fetchSales();
        } catch (err) {
            handleApiError(err);
        } finally {
            setDeleteModalVisible(false);
            setSaleToDelete(null);
            setIsBulkDelete(false);
        }
    };

    const cancelDeleteSale = () => {
        setDeleteModalVisible(false);
        setSaleToDelete(null);
        setIsBulkDelete(false);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedSales(sales.map(s => s._id || s.id));
        } else {
            setSelectedSales([]);
        }
    };

    const handleSelectSale = (saleId, checked) => {
        if (checked) {
            setSelectedSales(prev => [...prev, saleId]);
        } else {
            setSelectedSales(prev => prev.filter(id => id !== saleId));
        }
    };

    const handleExpandSale = (saleId) => {
        setExpandedSaleId(prev => prev === saleId ? null : saleId);
    };

    if (loading) return <div>Loading sales data...</div>;

    return (
        <div className="page-container">
            <div className="page-header">
                <h2 className="page-title">Sales History</h2>
                <div className="header-actions">
                    {selectedSales.length > 0 && (user.role === 'Admin' || user.role === 'Co-admin') && (
                        <button className="btn-danger" onClick={triggerBulkDelete} style={{ backgroundColor: 'var(--color-danger)', color: 'white' }}>
                            <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                            Delete Selected ({selectedSales.length})
                        </button>
                    )}
                    <button className="btn-export" onClick={() => fetchAndDownload(`${API_URL}/api/sales/export`, 'sales.csv')}>
                        <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path></svg>
                        Export Sales
                    </button>
                </div>
            </div>
            <div className="data-display">
                <h3>All Sales Transactions</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                {(user.role === 'Admin' || user.role === 'Co-admin') && (
                                    <th style={{ width: '40px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            onChange={handleSelectAll}
                                            checked={sales.length > 0 && selectedSales.length === sales.length}
                                            disabled={sales.length === 0}
                                        />
                                    </th>
                                )}
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Payment Method</th>
                                <th>Total Amount</th>
                                {(user.role === 'Admin' || user.role === 'Co-admin') && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map(sale => {
                                const saleId = sale._id || sale.id;
                                return (
                                    <React.Fragment key={saleId}>
                                        <tr onClick={() => handleExpandSale(saleId)} style={{ cursor: 'pointer', backgroundColor: expandedSaleId === saleId ? 'var(--color-bg-light)' : 'transparent' }}>
                                            {(user.role === 'Admin' || user.role === 'Co-admin') && (
                                                <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSales.includes(saleId)}
                                                        onChange={(e) => handleSelectSale(saleId, e.target.checked)}
                                                    />
                                                </td>
                                            )}
                                            <td>{new Date(sale.saleDate || sale.sale_date).toLocaleString('en-IN')}</td>
                                            <td>{sale.customer?.name || sale.customer_name || 'N/A'}</td>
                                            <td>{sale.paymentMethod}</td>
                                            <td>₹{Number(sale.totalAmount).toFixed(2)}</td>
                                            {(user.role === 'Admin' || user.role === 'Co-admin') && (
                                                <td onClick={e => e.stopPropagation()}>
                                                    <div className="table-actions">
                                                        <button className="icon-btn delete" onClick={() => triggerDeleteSale(saleId)}>
                                                            <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                        {expandedSaleId === saleId && (
                                            <tr className="expanded-row">
                                                <td colSpan={(user.role === 'Admin' || user.role === 'Co-admin') ? 6 : 4} style={{ padding: '16px', backgroundColor: 'var(--color-bg-light)' }}>
                                                    {Array.isArray(sale.items) && sale.items.length > 0 ? (
                                                        <table className="inventory-table" style={{ marginTop: '8px', border: '1px solid var(--color-border)' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ backgroundColor: 'var(--color-surface)' }}>Item Name</th>
                                                                    <th style={{ backgroundColor: 'var(--color-surface)' }}>Quantity</th>
                                                                    <th style={{ backgroundColor: 'var(--color-surface)' }}>Sale Price</th>
                                                                    <th style={{ backgroundColor: 'var(--color-surface)' }}>Total Price</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sale.items.map((item, index) => {
                                                                    const validPrice = Number(item.priceAtSale || item.unitPrice || 0);
                                                                    const rowTotal = item.total ? Number(item.total) : (validPrice * item.quantity);
                                                                    return (
                                                                        <tr key={index}>
                                                                            <td>{item.nameAtSale || item.productName || 'Unknown Item'}</td>
                                                                            <td>{item.quantity}</td>
                                                                            <td>₹{validPrice.toFixed(2)}</td>
                                                                            <td>₹{rowTotal.toFixed(2)}</td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <p>No item details found for this sale.</p>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {deleteModalVisible && (
                <div className="modal-overlay" onClick={cancelDeleteSale}>
                    <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
                        <h3>{isBulkDelete ? 'Delete Selected Sales' : 'Delete Sale'}</h3>
                        <p>
                            Are you sure you want to delete {isBulkDelete ? `these ${selectedSales.length} selected sales` : 'this sale'}?<br />
                            <strong>This action will restore the stock levels for all included items.</strong>
                        </p>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={cancelDeleteSale}>Cancel</button>
                            <button className="confirm-btn danger" onClick={confirmDeleteSale}>Delete {isBulkDelete && `(${selectedSales.length})`}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SalesView;
